import { ref, computed } from 'vue';
import { RouteLocationNormalizedLoaded } from 'vue-router';
import { defineStore } from 'pinia';

interface Program {
	name: string;
	keywords?: string; // seo keywords as comma-separated string
	types: Record<string, boolean>;
	delivery: {
		online: boolean;
		on_campus: boolean;
	};
	expanded?: boolean;
	college?: {
		name: string;
		url?: string;
	};
	department?: {
		name: string;
		url?: string;
	};
	details?: Record<string, { url: string; label: string }>;
	customDetails?: string;
	interests: string[];
	program_type: string[];
}

export const useProgramStore = defineStore('programStore', () => {
	// state
	const startView = ref<'filter' | 'results' | 'default'>('default');
	const keyword = ref('');
	const interests = ref<string[]>([]);
	const types = ref<string[]>([]);
	const sortOrder = ref<'asc' | 'desc'>('asc');
	const programs = ref<Program[]>([]);

	async function loadProgramsFromDom() {
		const appEl = document.querySelector<HTMLTableElement>('#app[data-programs-path]');
		const path = appEl?.getAttribute('data-programs-path');

		if (path) {
			try {
				const res = await fetch(path);
				if (!res.ok) throw new Error(`Failed to fetch programs data from ${path}`);

				const data = await res.json();
				programs.value = data.programs.map((program: Program) => ({
					...program,
					expanded: false,
				}));
			} catch (err) {
				console.error('Error loading programs:', err);
			}
		}
	}

	// toggle sort order for program name
	function toggleSortOrder() {
		sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
	}

	// filtered list of programs
	const filteredPrograms = computed(() => {
		const filtered = programs.value.filter(program => {
			// Start with core searchable fields
			const keywordParts: string[] = [
				program.name,
				program.college?.name || '',
				program.department?.name || '',
			];

			// comma-separated string of additional keywords to use for search
			if (program.keywords) {
				const kwTokens = program.keywords
					.split(',')
					.map(s => s.trim())
					.filter(Boolean);
				keywordParts.push(...kwTokens);
			}

			// Include details labels
			if (program.details) {
				for (const detail of Object.values(program.details)) {
					keywordParts.push(detail.label);
				}
			}

			// Include customDetails if it's a string of raw HTML (from WYSIWYG)
			if (program.customDetails) {
				const parser = new DOMParser();
				const doc = parser.parseFromString(program.customDetails, 'text/html');
				const customText = doc.body.textContent || '';
				keywordParts.push(customText);
			}

			const keywordTarget = keywordParts.filter(Boolean).join(' ').toLowerCase();

			const keywordMatch =
				keyword.value.trim() === '' || keywordTarget.includes(keyword.value.toLowerCase());

			const interestsMatch =
				interests.value.length === 0 || interests.value.some(i => program.interests.includes(i));

			const typesMatch =
				types.value.length === 0 || types.value.some(t => program.program_type.includes(t));

			return keywordMatch && interestsMatch && typesMatch;
		});

		// Sort alphabetically
		return filtered.sort((a, b) => {
			const nameA = a.name.toLowerCase();
			const nameB = b.name.toLowerCase();
			return sortOrder.value === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
		});
	});

	// actions
	const setStartView = (mode: 'filter' | 'results' | 'default') => {
		startView.value = mode;
	};

	const setFilters = (data: { keyword?: string; interests?: string[]; types?: string[] }) => {
		if (data.keyword !== undefined) keyword.value = data.keyword;
		if (data.interests !== undefined) interests.value = data.interests;
		if (data.types !== undefined) types.value = data.types;
	};

	// show results with the filters from the query params
	function initFromRoute(route: RouteLocationNormalizedLoaded) {
		const keyword = (route.query.keyword as string) || '';
		const interests =
			typeof route.query.interests === 'string'
				? route.query.interests.split(',').filter(Boolean)
				: [];
		const types =
			typeof route.query.types === 'string' ? route.query.types.split(',').filter(Boolean) : [];

		setFilters({ keyword, interests, types });
		setStartView('results');
	}

	return {
		startView,
		keyword,
		interests,
		types,
		programs,
		filteredPrograms,
		sortOrder,
		loadProgramsFromDom,
		toggleSortOrder,
		setStartView,
		setFilters,
		initFromRoute,
	};
});
